import { useMemo } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Modal from "../../../components/base/Modal";
import { Button, Combobox, Input, Switch, Textarea, notifyError, notifySuccess } from "../../../components/ui";
import { useCreateCostProfile, useUpdateCostProfile } from "../../../features/costProfiles/mutations";
import { useVehicleClassCatalog } from "../../../features/costProfiles/queries";
import type { CostProfile, CostProfileInput } from "../../../features/costProfiles/types";
import {
  DRIVER_MODES,
  EXTRA_MODES,
  FUEL_TYPES,
  INSURANCE_MODES,
  VEHICLE_CLASSES,
  type DriverMode,
  type ExtraMode,
  type FuelType,
  type InsuranceMode,
  type VehicleClass,
} from "../../../features/quotes/types";

interface CostProfileModalProps {
  /** Perfil a editar (null = alta). */
  profile: CostProfile | null;
  /** Perfil base para duplicar (solo en alta). */
  seed?: CostProfile | null;
  onClose: () => void;
}

type FormValues = CostProfileInput;

interface Option {
  value: string;
  label: string;
}

const DEFAULTS: FormValues = {
  name: "",
  vehicle_class: "tractocamion_5e",
  axles: 5,
  currency: "MXN",
  fuel_type: "diesel",
  fuel: { price_per_liter_by_type: { diesel: 26.5, gasoline: 25, lpg: 12.5, cng: 15 }, km_per_liter: 2.4 },
  driver: { mode: "per_day", amount: 900 },
  viaticos_per_day: 450,
  wear_per_km: 2.5,
  telemetry_per_day: 25,
  insurance: { mode: "per_trip", amount: 0 },
  parking_per_night: 150,
  toll_axle_factor: 2.6,
  overhead_pct: 10,
  default_margin_pct: 20,
  driving_hours_per_day: 8,
  extras: [],
  is_default: false,
  notes: "",
};

function toForm(p: CostProfile | null | undefined, copySuffix: string): FormValues {
  if (!p) return DEFAULTS;
  const { id: _id, company_id: _c, createdAt: _ca, updatedAt: _ua, is_system_default: _s, ...rest } = p;
  void _id;
  void _c;
  void _ca;
  void _ua;
  void _s;
  return {
    ...DEFAULTS,
    ...rest,
    name: copySuffix ? `${p.name} ${copySuffix}` : p.name,
    fuel: { price_per_liter_by_type: { ...DEFAULTS.fuel.price_per_liter_by_type, ...(p.fuel?.price_per_liter_by_type ?? {}) }, km_per_liter: p.fuel?.km_per_liter ?? DEFAULTS.fuel.km_per_liter },
    extras: Array.isArray(p.extras) ? p.extras : [],
    notes: p.notes ?? "",
    is_default: copySuffix ? false : Boolean(p.is_default),
  };
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h4 className="border-b border-line pb-2 text-sm font-semibold text-content">{title}</h4>
      {children}
    </section>
  );
}

/** Alta / edición de un perfil de costos operativos. */
export function CostProfileModal({ profile, seed, onClose }: CostProfileModalProps) {
  const { t } = useTranslation("finanzasOperativas");
  const catalog = useVehicleClassCatalog();
  const createMut = useCreateCostProfile();
  const updateMut = useUpdateCostProfile();
  const isEdit = Boolean(profile);

  const { control, register, handleSubmit, setValue, watch, formState: { errors, isSubmitting } } = useForm<FormValues>({
    mode: "onBlur",
    defaultValues: toForm(profile ?? seed, profile ? "" : seed ? t("costProfiles.copySuffix") : ""),
  });
  const { fields: extraFields, append, remove } = useFieldArray({ control, name: "extras" });

  const vehicleClass = watch("vehicle_class");
  const fuelType = watch("fuel_type");
  const driverMode = watch("driver.mode");
  const insuranceMode = watch("insurance.mode");

  const classOptions: Option[] = useMemo(() => VEHICLE_CLASSES.map((id) => ({ value: id, label: t(`costProfiles.vehicleClasses.${id}`) })), [t]);
  const fuelOptions: Option[] = useMemo(() => FUEL_TYPES.map((id) => ({ value: id, label: t(`costProfiles.fuelTypes.${id}`) })), [t]);
  const driverOptions: Option[] = useMemo(() => DRIVER_MODES.map((id) => ({ value: id, label: t(`costProfiles.driverModes.${id}`) })), [t]);
  const insuranceOptions: Option[] = useMemo(() => INSURANCE_MODES.map((id) => ({ value: id, label: t(`costProfiles.insuranceModes.${id}`) })), [t]);
  const extraOptions: Option[] = useMemo(() => EXTRA_MODES.map((id) => ({ value: id, label: t(`costProfiles.extraModes.${id}`) })), [t]);
  const currencyOptions: Option[] = [{ value: "MXN", label: "MXN" }, { value: "USD", label: "USD" }];

  /** Al cambiar de clase se proponen ejes, rendimiento, combustible y factor del catálogo. */
  const applyClassDefaults = (cls: VehicleClass) => {
    const info = catalog.data?.vehicle_classes.find((c) => c.id === cls);
    setValue("vehicle_class", cls);
    if (!info) return;
    setValue("axles", info.axles);
    setValue("fuel.km_per_liter", info.default_km_per_liter);
    setValue("fuel_type", info.fuel_type);
    setValue("toll_axle_factor", info.default_toll_axle_factor);
  };

  const onSubmit = async (values: FormValues) => {
    try {
      if (profile) await updateMut.mutateAsync({ id: profile.id, data: values });
      else await createMut.mutateAsync(values);
      void notifySuccess(t("costProfiles.toasts.saved"));
      document.body.style.overflow = "";
      onClose();
    } catch (err) {
      void notifyError(t("costProfiles.toasts.error"), (err as Error)?.message);
    }
  };

  const num = { valueAsNumber: true as const };
  const money = (key: FuelType) => `fuel.price_per_liter_by_type.${key}` as const;

  return (
    <Modal
      title={isEdit ? t("costProfiles.edit") : t("costProfiles.new")}
      onClose={onClose}
      width="w-full max-w-3xl"
      content={
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <Group title={t("costProfiles.groups.general")}>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input
                label={t("costProfiles.fields.name")}
                containerClassName="sm:col-span-2"
                error={errors.name?.message}
                {...register("name", { required: t("common.requiredField") })}
              />
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-content">{t("costProfiles.fields.vehicleClass")}</label>
                <Combobox
                  inputId="cp-class"
                  options={classOptions}
                  value={classOptions.find((o) => o.value === vehicleClass) ?? null}
                  onChange={(opt) => applyClassDefaults(((opt as Option | null)?.value as VehicleClass) ?? "otro")}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              <Input label={t("costProfiles.fields.axles")} type="number" min={2} max={9} {...register("axles", num)} />
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-content">{t("costProfiles.fields.currency")}</label>
                <Controller
                  control={control}
                  name="currency"
                  render={({ field }) => (
                    <Combobox inputId="cp-currency" options={currencyOptions} value={currencyOptions.find((o) => o.value === field.value) ?? null} onChange={(opt) => field.onChange((opt as Option | null)?.value ?? "MXN")} menuPortalTarget={document.body} menuPosition="fixed" />
                  )}
                />
              </div>
              <Input label={t("costProfiles.fields.drivingHoursPerDay")} type="number" min={1} max={24} {...register("driving_hours_per_day", num)} />
            </div>
          </Group>

          <Group title={t("costProfiles.groups.fuel")}>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="flex flex-col gap-1.5 col-span-2">
                <label className="text-sm font-medium text-content">{t("costProfiles.fields.fuelType")}</label>
                <Combobox inputId="cp-fuel" options={fuelOptions} value={fuelOptions.find((o) => o.value === fuelType) ?? null} onChange={(opt) => setValue("fuel_type", ((opt as Option | null)?.value as FuelType) ?? "diesel")} menuPortalTarget={document.body} menuPosition="fixed" />
              </div>
              <Input label={t("costProfiles.fields.kmPerLiter")} hint={t("costProfiles.hints.kmPerLiter")} containerClassName="col-span-2" type="number" min={0.1} step="0.1" {...register("fuel.km_per_liter", num)} />
              {FUEL_TYPES.map((ft) => (
                <Input key={ft} label={`${t("costProfiles.fields.fuelPrices")} · ${t(`costProfiles.fuelTypes.${ft}`)}`} type="number" min={0} step="0.01" {...register(money(ft), num)} />
              ))}
            </div>
          </Group>

          <Group title={t("costProfiles.groups.driver")}>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-content">{t("costProfiles.fields.driverMode")}</label>
                <Combobox inputId="cp-driver-mode" options={driverOptions} value={driverOptions.find((o) => o.value === driverMode) ?? null} onChange={(opt) => setValue("driver.mode", ((opt as Option | null)?.value as DriverMode) ?? "per_day")} menuPortalTarget={document.body} menuPosition="fixed" />
              </div>
              <Input label={t("costProfiles.fields.driverAmount")} type="number" min={0} step="0.01" {...register("driver.amount", num)} />
              <Input label={t("costProfiles.fields.viaticosPerDay")} type="number" min={0} step="0.01" {...register("viaticos_per_day", num)} />
            </div>
          </Group>

          <Group title={t("costProfiles.groups.daily")}>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Input label={t("costProfiles.fields.wearPerKm")} type="number" min={0} step="0.01" {...register("wear_per_km", num)} />
              <Input label={t("costProfiles.fields.telemetryPerDay")} type="number" min={0} step="0.01" {...register("telemetry_per_day", num)} />
              <Input label={t("costProfiles.fields.parkingPerNight")} type="number" min={0} step="0.01" {...register("parking_per_night", num)} />
            </div>
          </Group>

          <Group title={t("costProfiles.groups.insurance")}>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-content">{t("costProfiles.fields.insuranceMode")}</label>
                <Combobox inputId="cp-ins-mode" options={insuranceOptions} value={insuranceOptions.find((o) => o.value === insuranceMode) ?? null} onChange={(opt) => setValue("insurance.mode", ((opt as Option | null)?.value as InsuranceMode) ?? "per_trip")} menuPortalTarget={document.body} menuPosition="fixed" />
              </div>
              <Input label={t("costProfiles.fields.insuranceAmount")} type="number" min={0} step="0.01" {...register("insurance.amount", num)} />
            </div>
          </Group>

          <Group title={t("costProfiles.groups.tolls")}>
            <Input label={t("costProfiles.fields.tollAxleFactor")} hint={t("costProfiles.hints.tollAxleFactor")} type="number" min={0.5} max={10} step="0.1" {...register("toll_axle_factor", num)} />
          </Group>

          <Group title={t("costProfiles.groups.margin")}>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input label={t("costProfiles.fields.overheadPct")} hint={t("costProfiles.hints.overhead")} type="number" min={0} max={100} step="0.5" {...register("overhead_pct", num)} />
              <Input label={t("costProfiles.fields.defaultMarginPct")} hint={t("costProfiles.hints.margin")} type="number" min={0} max={95} step="0.5" {...register("default_margin_pct", num)} />
            </div>
          </Group>

          <Group title={t("costProfiles.groups.extras")}>
            <div className="space-y-2">
              {extraFields.map((f, i) => (
                <div key={f.id} className="flex flex-wrap items-end gap-2">
                  <Input containerClassName="min-w-40 flex-1" size="sm" label={i === 0 ? t("costProfiles.fields.extraLabel") : undefined} {...register(`extras.${i}.label` as const, { required: true })} />
                  <div className="flex w-40 flex-col gap-1.5">
                    {i === 0 && <label className="text-sm font-medium text-content">{t("costProfiles.fields.extraMode")}</label>}
                    <Controller
                      control={control}
                      name={`extras.${i}.mode` as const}
                      render={({ field }) => (
                        <Combobox inputId={`cp-extra-mode-${i}`} size="sm" options={extraOptions} value={extraOptions.find((o) => o.value === field.value) ?? null} onChange={(opt) => field.onChange(((opt as Option | null)?.value as ExtraMode) ?? "per_trip")} menuPortalTarget={document.body} menuPosition="fixed" />
                      )}
                    />
                  </div>
                  <Input containerClassName="w-32" size="sm" label={i === 0 ? t("costProfiles.fields.extraAmount") : undefined} type="number" min={0} step="0.01" {...register(`extras.${i}.amount` as const, num)} />
                  <Button variant="ghost" size="sm" iconOnly className="shrink-0" icon={<i className="ri-close-line" />} onClick={() => remove(i)} />
                </div>
              ))}
              <Button type="button" variant="dashed" size="sm" fullWidth icon={<i className="ri-add-line" />} onClick={() => append({ label: "", mode: "per_trip", amount: 0 })}>
                {t("costProfiles.addExtra")}
              </Button>
            </div>
          </Group>

          <Textarea label={t("costProfiles.fields.notes")} rows={2} {...register("notes")} />

          <div className="flex items-center gap-3">
            <Controller control={control} name="is_default" render={({ field }) => <Switch checked={Boolean(field.value)} onChange={field.onChange} aria-label={t("costProfiles.fields.isDefault")} />} />
            <span className="text-sm text-content">{t("costProfiles.fields.isDefault")}</span>
          </div>

          <div className="flex gap-3 border-t border-line pt-4">
            <Button type="button" variant="outline" size="md" shape="pill" className="flex-1" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" size="md" shape="pill" className="flex-1" loading={isSubmitting || createMut.isPending || updateMut.isPending}>
              {t("common.save")}
            </Button>
          </div>
        </form>
      }
    />
  );
}
